import { createHash } from 'node:crypto';

import { withBxApiContext } from 'helpers/withBxApiContext';
import bxApi from 'services/bxApi';

import { ROUTE_TYPES } from 'app/(clickouts)/constants';
import {
  BuyerIntentCompareView,
  getBuyerIntentCategoryId,
  getBuyerIntentCompareProductIds,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import {
  buildRemoveComparisonUrl,
  transformAddToCompareAlternatives,
} from 'helpers/dataTransformers/compareBxApi';
import { transformCompareProduct } from 'helpers/dataTransformers/listingDetailBxApi';
import gaDimensionsBuilder from 'helpers/gaDimensionsBuilder';
import { getGeoipDetails } from 'helpers/geoipDetector';
import { getAppData, setCacheTags } from 'helpers/initializePage';
import { hasPplCategory } from 'helpers/liveChat';
import Compare from 'screens/compare';
import getAppService from 'services/getapp';
import initializeExperiments from 'services/launchDarkly';
import { getPplCategories } from 'services/pplCategories/pplCategories';

Compare.ddogName = 'compare';

const getClientIp = (req) => {
  const ip =
    req.headers['cf-connecting-ip'] ||
    req.headers['x-forwarded-for']?.split(',')[0]?.trim() ||
    req.socket?.remoteAddress ||
    '';
  return ip.trim();
};

const toRelativePath = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url, 'https://www.getapp.com');
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

const buildCompareHeadInfo = (
  names,
  listings,
  countryCode,
  geoipProvider,
  headerSlug,
  clientIp,
  comparePageInfo,
  baseHead
) => {
  const year = new Date().getFullYear();
  const joined = names.join(' vs ');
  const title = names.length === 2 ? joined : `${joined} Comparison`;
  const pageHeader = `${joined} Comparison (${year})`;
  const description =
    names.length === 2
      ? `Compare ${names[0]} and ${names[1]} based on features, pricing, verified reviews, integrations & more on GetApp. Find out which software is best for your business in ${year}.`
      : null;

  const mainListing = listings[0];
  const compareProductUuids = listings
    .filter(Boolean)
    .map((l) => l?.global_product_id)
    .filter(Boolean)
    .join('_');

  const listingsCount = listings.filter(Boolean).length;
  const premiumsCount = listings.filter((l) => l?.is_premium).length;
  const basicsCount = listings.filter((l) => l && !l.is_premium).length;
  const featuresCount = new Set(
    listings.filter(Boolean).flatMap((l) => l?.key_features || [])
  ).size;
  const integrationsCount = new Set(
    listings
      .filter(Boolean)
      .flatMap((l) =>
        (l?.integrations || []).map((i) => i?.name).filter(Boolean)
      )
  ).size;
  const hasVideoComparisonReviews = listings
    .filter(Boolean)
    .some((l) => l?.video_comparison_reviews?.length)
    ? 1
    : 0;
  const compareProductSeoIds = listings
    .filter(Boolean)
    .map((l) => l?.id)
    .filter(Boolean)
    .join('_');
  const latestModifiedOn = listings
    .filter(Boolean)
    .reduce(
      (latest, l) => (!latest || l?.modified > latest ? l?.modified : latest),
      null
    );

  const modified = latestModifiedOn
    ? Number.parseInt(
        new Date(latestModifiedOn).toISOString().slice(0, 10).replace(/-/g, ''),
        10
      )
    : null;

  const isIndexable = comparePageInfo?.isPublished === true;
  const canonicalUrl = comparePageInfo?.url || null;
  const robots = isIndexable ? null : 'noindex,nofollow';
  const linkRel =
    isIndexable && canonicalUrl
      ? [{ rel: 'canonical', url: canonicalUrl }]
      : [];

  return {
    ...baseHead,
    title,
    page_header: pageHeader,
    meta: {
      robots,
      description,
      og: {
        og_logo: '/i/og_logo.jpg',
        og_title: title,
        og_desc: description,
        twitter_site_content: '@GetApp',
        extra_metas: [
          { name: 'description', content: description },
          { name: 'robots', content: robots },
        ],
      },
    },
    link_rel: linkRel,
    google_analytics: {
      content_group: ROUTE_TYPES.COMPARE,
      dimensions: gaDimensionsBuilder({
        route_name: ROUTE_TYPES.COMPARE,
        listing_slug: mainListing?.slug,
        page_product_name: mainListing?.name,
      }),
      metrics: [
        { number: 3, value: 1 },
        { number: 4, value: premiumsCount },
        { number: 5, value: basicsCount },
        { number: 6, value: mainListing?.reviews?.total_number ?? 0 },
        { number: 10, value: listingsCount - 1 },
        ...(mainListing?.rating_avg
          ? [{ number: 8, value: Math.round(mainListing.rating_avg * 2) }]
          : []),
        { number: 28, value: countryCode },
        { number: 29, value: geoipProvider },
      ],
    },
    data_layer: [
      {
        routeName: ROUTE_TYPES.COMPARE,
        hashID: createHash('md5').update(clientIp).digest('hex'),
        geoipCountryCode: countryCode,
        geoipProvider,
        contentGroupGA4: 'Compare/Software',
        productName: mainListing?.name || null,
        productVendor: mainListing?.company_name || null,
        productHeader: headerSlug || null,
        productID: mainListing?.id || null,
        globalProductId: mainListing?.global_product_id || null,
        reviewQuantity: mainListing?.reviews?.total_number || null,
        reviewRating: mainListing?.rating_avg || null,
        ppcMonetizationStatus: mainListing?.is_premium ? 'Upgraded' : 'Basic',
        compareProductUuids: compareProductUuids || null,
        listingsCount,
        premiumsCount,
        basicsCount,
        featuresCount,
        integrationsCount,
        hasVideoComparisonReviews,
        compareProductSeoIds: compareProductSeoIds || null,
        modified,
      },
    ],
  };
};

export const getServerSideProps = withBxApiContext('compare', async (ctx) => {
  const { slug, apps } = ctx.query || {};
  const { countryCode, provider: geoipProvider } = getGeoipDetails(ctx);
  const allSlugs = [slug, ...(apps ? apps.split('-vs-') : [])].filter(Boolean);

  const { getUser } = getAppService(ctx.req);

  const [
    hreflangs,
    experiments,
    user,
    pplCategories,
    comparePageInfo,
    ...bxApiProducts
  ] = await Promise.all([
    bxApi.getCompareHreflangsBySlugs(allSlugs).catch(() => []),
    initializeExperiments({
      ctx,
      pageCategory: ctx.query.header,
      pageType: ROUTE_TYPES.COMPARE,
    }),
    getUser().catch(() => null),
    getPplCategories({ countryCode }),
    bxApi.checkComparePageIndexable(allSlugs).catch(() => null),
    ...allSlugs.map((s) =>
      bxApi
        .getProductBySlug({
          slug: s,
          countryCode,
          categorySelection: ['HIGHEST_CPC_DIRECTORY', 'PRIMARY'],
          topFeaturesCount: 150,
        })
        .catch(() => null)
    ),
  ]);

  if (!bxApiProducts[0]) {
    return { notFound: true };
  }

  if (allSlugs.length < 2) {
    return {
      redirect: {
        destination: `/${ctx.query.header}-software/a/${allSlugs[0]}/alternatives/`,
        statusCode: 301,
      },
    };
  }

  const canonicalUrl = comparePageInfo?.url;
  if (canonicalUrl) {
    const canonicalPath = new URL(canonicalUrl, 'http://localhost').pathname;
    const currentPath = ctx.req.url.split('?')[0];
    if (currentPath !== canonicalPath) {
      return {
        redirect: {
          destination: canonicalPath,
          statusCode: 301,
        },
      };
    }
  }

  const appData = { ...getAppData(ctx, {}), experiments, hreflangs };
  appData.header = { ...appData.header, user };

  const bxApiBySlug = {};
  allSlugs.forEach((s, i) => {
    if (bxApiProducts[i]) bxApiBySlug[s] = bxApiProducts[i];
  });

  const extendedListings = allSlugs
    .map((s) => bxApiBySlug[s])
    .filter(Boolean)
    .map((bxProduct) => transformCompareProduct(bxProduct))
    .filter(Boolean);

  // Compute remove_comparison URLs on the frontend instead of using backend data
  // For non-premium products, use detail URL as fallback for cta
  extendedListings.forEach((listing, index) => {
    if (listing?.cta_urls) {
      listing.cta_urls.remove_comparison = buildRemoveComparisonUrl(
        extendedListings,
        index
      ) || { url: null };
      listing.cta_urls.cta = listing.cta_urls.cta || listing.cta_urls.detail;
    }
  });

  const bxCategory = bxApiProducts[0]?.category;
  const categorySlug = bxCategory?.slug || '';
  const categoryUrl =
    toRelativePath(bxCategory?.url) ||
    `/${ctx.query.header}-software/${categorySlug}/`;
  const listingNames = extendedListings
    .filter(Boolean)
    .map((l) => l?.name)
    .filter(Boolean);
  appData.route = ROUTE_TYPES.COMPARE;
  appData.header = {
    ...appData.header,
    breadcrumb: [
      ...(bxCategory
        ? [
            {
              type: 'category',
              label: bxCategory.name,
              slug: bxCategory.slug,
              url: categoryUrl,
            },
          ]
        : []),
      {
        label: listingNames.join(' vs '),
        url: `/${ctx.query.header}-software/a/${allSlugs[0]}/compare/${allSlugs.slice(1).join('-vs-')}/`,
      },
    ],
  };
  appData.head = {
    ...buildCompareHeadInfo(
      listingNames,
      extendedListings,
      countryCode,
      geoipProvider,
      ctx.query.header,
      getClientIp(ctx.req),
      comparePageInfo,
      appData.head
    ),
    hasLiveChatEnabled: hasPplCategory(pplCategories, categorySlug),
  };

  ctx.req.APP_DATA = appData; // We pass in current Request this info to Document in order to avoid double calculation.
  setCacheTags(ctx, ROUTE_TYPES.COMPARE);

  const [, ...comparedSlugs] = allSlugs;
  const bxAlternatives = transformAddToCompareAlternatives({
    mainProduct: bxApiProducts[0],
    comparedSlugs,
  });

  const keyFeaturesAggregate = [
    ...new Set(extendedListings.flatMap((l) => l?.key_features || [])),
  ].sort();
  const integrationsAggregate = [
    ...new Set(
      extendedListings.flatMap((l) =>
        (l?.integrations || []).map((i) => i?.name).filter(Boolean)
      )
    ),
  ].sort();
  const listingsWithKeyFeatures = extendedListings.map((l) => ({
    ...l,
    features: (l?.key_features || []).map((name) => ({ name })),
  }));

  return {
    props: {
      appData,
      compareData: {
        alternatives: bxAlternatives,
        title: appData.head.page_header,
        listings: listingsWithKeyFeatures,
        features: keyFeaturesAggregate,
        integrations: integrationsAggregate,
        comparisonReviews: { first: null, last: null },
      },
    },
  };
});

const ComparePage = (props) => {
  const productIds = getBuyerIntentCompareProductIds(props.compareData);
  const categoryId = getBuyerIntentCategoryId(props.appData);

  if (productIds.length < 2) {
    warnMissingBuyerIntentIds('page:compare', {
      productIds: props.compareData?.listings?.map(
        (listing) => listing?.global_product_id
      ),
      categoryId,
    });

    return <Compare {...props} />;
  }

  return (
    <BuyerIntentCompareView
      productIds={productIds}
      categoryId={categoryId}
      sourceLocation="page:compare"
    >
      <Compare {...props} />
    </BuyerIntentCompareView>
  );
};

ComparePage.ddogName = Compare.ddogName;
ComparePage.Layout = Compare.Layout;
ComparePage.hasBuyerIntentPageView = true;

export default ComparePage;
